<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { SidebarMenuItem, MenuPermission, MenuClickEvent } from '@/types/sidebar-menu.types'
import JIcon from '@/components/atoms/JIcon.vue'
import { cn, hasMenuPermission } from '@/lib/utils'

/**
 * JDynamicMenuItem - 재귀적 메뉴 아이템 컴포넌트
 * Recursive Menu Item Component
 * 
 * @description
 * 다단계 메뉴 구조를 재귀적으로 렌더링하는 컴포넌트입니다.
 * 폴더 타입 메뉴는 확장/축소가 가능하고, 링크 타입 메뉴는 클릭 시 라우팅합니다.
 */

type StyleType = 'default' | 'minimal'

const props = withDefaults(
  defineProps<{
    /** 메뉴 아이템 */
    item: SidebarMenuItem
    /** 메뉴 레벨 (들여쓰기용, 0부터 시작) */
    level?: number
    /** 권한 목록 */
    permissions?: MenuPermission[]
    /** 활성화된 메뉴 경로 */
    activePath?: string
    /** 확장된 메뉴 키 목록 */
    expandedKeys?: Set<number | string>
    /** 즐겨찾기 메뉴 키 목록 */
    favorites?: (number | string)[]
    /** 즐겨찾기 변경 핸들러 */
    onFavoriteToggle?: (menuKey: number | string | undefined) => void
    /** 즐겨찾기 확인 함수 */
    isFavorite?: (menuKey: number | string | undefined) => boolean
    /** 스타일 타입 */
    styletype?: StyleType
    /** 추가 CSS 클래스 */
    className?: string
    /** 최대 깊이 제한 (무한 루프 방지, 기본값: 10) */
    maxDepth?: number
    /** 네비게이션 비활성화 (true일 때 router.push 건너뛰고 emit만 수행) */
    disableNavigation?: boolean
    /** 활성화된 메뉴 키 (menuKey 기반 활성화, activePath보다 우선) */
    activeKey?: number | string | null
  }>(),
  {
    level: 0,
    permissions: () => [],
    expandedKeys: () => new Set(),
    favorites: () => [],
    styletype: 'default',
    maxDepth: 10,
    disableNavigation: false,
    activeKey: null,
  },
)

const emit = defineEmits<{
  /** 메뉴 클릭 이벤트 */
  menuClick: [event: MenuClickEvent]
  /** 확장 상태 변경 이벤트 */
  expandChange: [menuKey: number | string | undefined, expanded: boolean]
}>()

const router = useRouter()

/**
 * 권한 체크 함수
 * Permission check function
 * hasMenuPermission 유틸리티 함수를 사용하여 일관성 유지
 */
const checkPermission = computed(() => {
  return hasMenuPermission(props.item.menuKey, props.permissions)
})

/**
 * 메뉴가 활성화되어 있는지 여부
 * activeKey가 제공되면 menuKey 매칭, 아니면 경로 매칭
 */
const isActive = computed(() => {
  // activeKey가 제공되면 menuKey 기반 매칭 (우선순위 높음)
  if (props.activeKey !== undefined && props.activeKey !== null) {
    return props.item.menuKey === props.activeKey
  }
  // 경로 기반 매칭 (기본 동작)
  if (!props.item.path || !props.activePath) return false
  return props.activePath === props.item.path
})

/**
 * 메뉴 타입이 폴더인지 여부
 * 순환 참조 방지: children이 유효한 배열인지 확인
 */
const isFolder = computed(() => {
  return props.item.menuType === 'F' || (Array.isArray(props.item.children) && props.item.children.length > 0)
})

/**
 * 메뉴가 확장되어 있는지 여부
 */
const isExpanded = computed(() => {
  if (!isFolder.value) return false
  const key = props.item.menuKey || props.item.label
  return props.expandedKeys?.has(key) ?? false
})

/**
 * 메뉴가 비활성화되어 있는지 여부
 */
const isDisabled = computed(() => {
  return props.item.disabled || !checkPermission.value
})

/**
 * 레벨별 들여쓰기 스타일
 * 모든 레벨에서 동일한 padding 사용 (들여쓰기는 컨테이너의 ml로 조절)
 */
const indentStyle = computed(() => {
  return { paddingLeft: '8px', paddingRight: '8px' }
})



/**
 * 메뉴 클릭 핸들러
 */
const handleMenuClick = (event: MouseEvent) => {
  if (isDisabled.value) {
    event.preventDefault()
    return
  }

  if (isFolder.value) {
    // 폴더 타입: 확장/축소 토글
    const key = props.item.menuKey || props.item.label
    const newExpanded = !isExpanded.value
    emit('expandChange', key, newExpanded)
    // 폴더도 메뉴 클릭 이벤트 발생
    emit('menuClick', {
      menuItem: props.item,
      path: [props.item],
      event,
    })
  } else {
    // 링크 타입: 라우팅 (disableNavigation이 false일 때만)
    if (!props.disableNavigation && props.item.path) {
      router.push(props.item.path)
    }
    
    // 메뉴 클릭 이벤트 발생
    emit('menuClick', {
      menuItem: props.item,
      path: [props.item], // 단순화된 경로 (필요시 부모 경로 포함하도록 확장 가능)
      event,
    })
  }
}

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  itemClass: string
  labelClass: string
  iconSize: 'sm' | 'md'
}> = {
  default: {
    itemClass: 'flex items-center gap-1.5 py-0.5 rounded-md cursor-pointer transition-colors group',
    labelClass: 'flex-1 truncate',
    iconSize: 'sm',
  },
  minimal: {
    itemClass: 'flex items-center gap-1 py-0.5 rounded-md cursor-pointer transition-colors group',
    labelClass: 'flex-1 truncate',
    iconSize: 'sm', // JIcon은 'xs'를 지원하지 않으므로 'sm' 사용
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * Chevron 아이콘 컴포넌트
 */
const ChevronIcon = computed(() => {
  return isExpanded.value ? 'chevronDown' : 'chevronRight'
})

/**
 * 하위 메뉴 갯수 계산 (재귀적으로 모든 하위 메뉴 포함)
 * 폴더 타입인 경우에만 표시
 */
const childrenCount = computed(() => {
  if (!isFolder.value || !Array.isArray(props.item.children)) {
    return 0
  }
  
  const countChildren = (items: SidebarMenuItem[]): number => {
    let count = 0
    for (const item of items) {
      count++ // 현재 아이템 카운트
      if (Array.isArray(item.children) && item.children.length > 0) {
        count += countChildren(item.children) // 재귀적으로 하위 메뉴 카운트
      }
    }
    return count
  }
  
  return countChildren(props.item.children)
})
</script>

<template>
  <div :class="cn('w-full', className)">
    <!-- 메뉴 아이템 -->
    <div
      :class="cn(
        preset.itemClass,
        {
          'bg-primary/5 text-primary border-l-2 border-primary shadow-sm': isActive,
          'hover:bg-accent/50': !isDisabled && !isActive,
          'opacity-50 cursor-not-allowed': isDisabled,
          'font-medium': isActive,
          'font-semibold': isFolder, // 폴더인 경우 볼드체
        }
      )"
      :style="indentStyle"
      @click="handleMenuClick"
    >
      <!-- Chevron 아이콘 (폴더 타입만, 덜 굵게) -->
      <JIcon
        v-if="isFolder"
        :name="ChevronIcon"
        :size="preset.iconSize"
        class="flex-shrink-0 opacity-60"
        style="stroke-width: 1.5;"
      />
      <span v-else class="w-4 flex-shrink-0" /> <!-- 폴더가 아닐 때 공간 확보 -->

      <!-- 메뉴 아이콘 (폴더가 아닌 경우만 표시) -->
      <JIcon
        v-if="item.icon && !isFolder"
        :name="item.icon"
        :size="preset.iconSize"
        class="flex-shrink-0"
      />

      <!-- 메뉴 라벨 (폴더: text-xs, 링크: text-[11px]) -->
      <span :class="[preset.labelClass, isFolder ? 'text-xs' : 'text-[11px]']">{{ item.label }}</span>
      
      <!-- 하위 메뉴 갯수 (폴더인 경우만) -->
      <span
        v-if="isFolder && childrenCount > 0"
        :class="cn(
          'text-muted-foreground ml-1 flex-shrink-0',
          props.styletype === 'minimal' ? 'text-[10px]' : 'text-xs'
        )"
      >
        ({{ childrenCount }})
      </span>
      
      <!-- 즐겨찾기 버튼 (menuType이 L인 경우만) -->
      <button
        v-if="item.menuKey && item.menuType === 'L' && onFavoriteToggle"
        :class="cn(
          'opacity-0 group-hover:opacity-100 transition-opacity hover:bg-accent rounded flex-shrink-0',
          props.styletype === 'minimal' ? 'p-0.5' : 'p-1',
          isFavorite && isFavorite(item.menuKey) && 'opacity-100'
        )"
        @click.stop="onFavoriteToggle(item.menuKey)"
      >
        <JIcon
          :name="isFavorite && isFavorite(item.menuKey) ? 'star' : 'star'"
          :size="preset.iconSize"
          :class="isFavorite && isFavorite(item.menuKey) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'"
        />
      </button>
    </div>

    <!-- 하위 메뉴 (폴더 타입이고 확장된 경우) -->
    <!-- 깊이 제한 체크: maxDepth를 초과하지 않는 경우에만 렌더링 -->
    <div
      v-if="isFolder && isExpanded && item.children && Array.isArray(item.children) && item.children.length > 0 && (level + 1) < maxDepth"
      class="border-l border-border/60 ml-[14px] pl-[6px]"
    >
      <JDynamicMenuItem
        v-for="(child, index) in item.children"
        :key="child.menuKey || child.label || index"
        :item="child"
        :level="level + 1"
        :max-depth="maxDepth"
        :permissions="permissions"
        :active-path="activePath"
        :expanded-keys="expandedKeys"
        :favorites="favorites"
        :on-favorite-toggle="onFavoriteToggle"
        :is-favorite="isFavorite"
        :styletype="styletype"
        :disable-navigation="disableNavigation"
        :active-key="activeKey"
        @menu-click="emit('menuClick', $event)"
        @expand-change="(menuKey, expanded) => emit('expandChange', menuKey, expanded)"
      />
    </div>
  </div>
</template>
